import { CheckBox, Text } from "@ui-kitten/components";
import { validationSchemas } from "constants/";
import {
  BuyFlowUser,
  PlanPackageUserFieldDetails,
  TenantUserFieldDetails,
  UserFieldDetails,
} from "features/types";
import { Field, Formik } from "formik";
import { useAppSelector } from "hooks/useAppSelector";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useTenantUserFields, { getFieldLabel } from "hooks/useTenantUserFields";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React, { useRef, useState } from "react";
import {
  NativeSyntheticEvent,
  Platform,
  Pressable,
  TextInputKeyPressEventData,
  View,
} from "react-native";
import { getEmailValidation } from "utils/planPackageUtils";
import { object, string as yupString } from "yup";
import Button from "./Button";
import DatePicker from "./DatePicker/DatePicker";
import DatePickerInput from "./DatePicker/DatePickerInput";
import FanRallyTOSDialog from "./FanRallyTOSDialog/FanRallyTOSDialog";
import ProfileAvatar from "./ProfileAvatar";
import TextInput from "./TextInput";

interface NewMemberFormProps {
  initialValues: BuyFlowUser;
  onSubmit: (user: BuyFlowUser) => void;
  btnText?: string;
  showImageUploader?: boolean;
  includePassword?: boolean;
  editingChild?: boolean;
  TOSRequired?: boolean;
  creatingAssociateAccount?: boolean;
}

// auto focus the next input field
export const handleOnSubmitEditing = (
  ref: any,
  e?: NativeSyntheticEvent<TextInputKeyPressEventData>
) => {
  if (Platform.OS === "web") {
    if (e?.nativeEvent.key === "Tab" || e?.nativeEvent.key === "Enter") {
      e.preventDefault();
      ref && ref.focus();
    }
  } else {
    if (e?.nativeEvent.key === undefined || e?.nativeEvent.key === "Enter") {
      ref && ref.focus();
    }
  }
};

export const RenderUserFields = ({
  type,
  userField,
  index,
  userFieldRefs,
  nextUserFieldRefs,
  name,
}: {
  type: UserFieldDetails["type"];
  userField: TenantUserFieldDetails | PlanPackageUserFieldDetails;
  index: number;
  userFieldRefs: React.MutableRefObject<any[]>; // array containing current tenant user fields/plan package user fields refs
  nextUserFieldRefs?: React.MutableRefObject<any[]>; // another array containing another tenant user fields/plan package user fields refs
  name: string;
}) => {
  const submitEditing = (e?: any) => {
    // if nextUserFieldRefs is defined, after the last user field of current userFields(most likely tenant user fields), finishes entering
    // and when user presses tab/enter, move focus to the first input of nextUserFieldRefs(most likely plan package user fields)
    if (
      index === userFieldRefs.current.length - 1 &&
      nextUserFieldRefs !== undefined
    ) {
      handleOnSubmitEditing(nextUserFieldRefs.current[index], e);
    } else {
      handleOnSubmitEditing(userFieldRefs.current[index + 1], e);
    }
  };

  return (
    <View style={{ flex: 0.7 }}>
      {type === "DATETIME" ? (
        <Field
          component={DatePicker}
          name={name}
          label={getFieldLabel(userField)}
          onSubmitEditing={submitEditing}
          onKeyPress={submitEditing}
          innerRef={(ref: any) => {
            userFieldRefs.current[index] = ref;
          }}
        ></Field>
      ) : type === "DATE" ? (
        <Field
          component={DatePickerInput}
          name={name}
          label={getFieldLabel(userField)}
          innerRef={(ref: any) => {
            userFieldRefs.current[index] = ref;
          }}
          userFieldType={type}
          onSubmitEditing={submitEditing}
          onKeyPress={submitEditing}
        ></Field>
      ) : (
        <Field
          component={TextInput}
          name={name}
          innerRef={(ref: any) => {
            userFieldRefs.current[index] = ref;
          }}
          userFieldType={type}
          label={getFieldLabel(userField)}
          onSubmitEditing={submitEditing}
          onKeyPress={submitEditing}
        ></Field>
      )}
    </View>
  );
};
const NewMemberForm: React.FC<NewMemberFormProps> = ({
  initialValues,
  onSubmit,
  btnText,
  showImageUploader = true,
  includePassword = false,
  editingChild = false,
  TOSRequired = false,
  creatingAssociateAccount = false,
}) => {
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const requirePlayerId = tenantConfig?.requirePlayerIdForSignup ?? false;

  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const playerIdRef = useRef(null);

  const planPackage = useAppSelector(
    (state) => state.buyFlow.selectedPlanPackage
  );
  const {
    tenantUserFields,
    fieldRefs: tenantUserFieldRefs,
    tenantFieldsValidation,
  } = useTenantUserFields();
  const {
    planPackageUserFields,
    fieldRefs: planPackageUserFieldRefs,
    planPackageFieldsValidation,
  } = usePlanPackageUserFields(planPackage?.id);

  const [TOSDialog, setTOSDialog] = useState(false);
  const [TOSChecked, setTOSChecked] = useState(false);

  // if creating associate account, do not apply plan package level email validation
  // because we are not signing up this account with a plan package yet.
  const emailValidation = creatingAssociateAccount
    ? undefined
    : getEmailValidation(planPackage);

  const { basicValidation, passwordValidation, editingChildValidation } =
    validationSchemas({
      emailValidation,
    });

  const playerIdValidation: Record<string, any> = requirePlayerId && !creatingAssociateAccount
    ? { playerId: yupString().required(i18n.t("errors.form.required")) }
    : {};

  let validationSchema: any = basicValidation;
  if (includePassword) {
    validationSchema = { ...validationSchema, ...passwordValidation };
  } else if (editingChild) {
    validationSchema = { ...editingChildValidation };
  }

  return (
    <View>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={object({
          ...validationSchema,
          ...playerIdValidation,
          ...tenantFieldsValidation,
          ...planPackageFieldsValidation,
        })}
      >
        {({ values, handleSubmit, isValid, setFieldValue }) => (
          <View>
            {showImageUploader && (
              <View style={{ alignSelf: "center", marginVertical: 15 }}>
                <ProfileAvatar
                  hasImageIdentifier={!!values.imageIdentifier}
                  source={{ uri: values.imageIdentifier }}
                  editButton
                  onChange={(uri) => setFieldValue("imageIdentifier", uri)}
                />
              </View>
            )}
            <Field
              component={TextInput}
              name="firstName"
              label={i18n.t("labels.firstName")}
              onSubmitEditing={() => handleOnSubmitEditing(lastNameRef.current)}
              onKeyPress={(e: any) =>
                handleOnSubmitEditing(lastNameRef.current, e)
              }
            />
            <Field
              component={TextInput}
              name="lastName"
              label={i18n.t("labels.lastName")}
              onSubmitEditing={() => {
                if (editingChild) {
                  handleOnSubmitEditing(
                    requirePlayerId && !creatingAssociateAccount
                      ? playerIdRef.current
                      : tenantUserFieldRefs.current[0]
                  );
                } else {
                  handleOnSubmitEditing(emailRef.current);
                }
              }}
              onKeyPress={(e: any) => {
                if (editingChild) {
                  handleOnSubmitEditing(
                    requirePlayerId && !creatingAssociateAccount
                      ? playerIdRef.current
                      : tenantUserFieldRefs.current[0],
                    e
                  );
                } else {
                  handleOnSubmitEditing(emailRef.current, e);
                }
              }}
              innerRef={(ref: any) => (lastNameRef.current = ref)}
            />

            {/* {getError(errors, touched, "lastName")} */}
            {editingChild === false && (
              <>
                <Field
                  component={TextInput}
                  name="email"
                  label={i18n.t("labels.email")}
                  innerRef={(ref: any) => (emailRef.current = ref)}
                  onSubmitEditing={() =>
                    handleOnSubmitEditing(phoneRef.current)
                  }
                  onKeyPress={(e: any) =>
                    handleOnSubmitEditing(phoneRef.current, e)
                  }
                />
                <Field
                  component={TextInput}
                  name="phone"
                  label={i18n.t("labels.phone")}
                  innerRef={(ref: any) => (phoneRef.current = ref)}
                  onSubmitEditing={() => {
                    if (includePassword) {
                      handleOnSubmitEditing(passwordRef.current);
                    } else if (requirePlayerId && !creatingAssociateAccount) {
                      handleOnSubmitEditing(playerIdRef.current);
                    } else if (tenantUserFieldRefs.current.length > 0) {
                      handleOnSubmitEditing(tenantUserFieldRefs.current[0]);
                    }
                  }}
                  onKeyPress={(e: any) => {
                    if (includePassword) {
                      handleOnSubmitEditing(passwordRef.current, e);
                    } else if (requirePlayerId && !creatingAssociateAccount) {
                      handleOnSubmitEditing(playerIdRef.current, e);
                    } else if (tenantUserFieldRefs.current.length > 0) {
                      handleOnSubmitEditing(tenantUserFieldRefs.current[0], e);
                    }
                  }}
                />
              </>
            )}
            {requirePlayerId && !creatingAssociateAccount && (
              <Field
                component={TextInput}
                name="playerId"
                label="Player ID"
                innerRef={(ref: any) => {
                  playerIdRef.current = ref;
                }}
                onSubmitEditing={() =>
                  handleOnSubmitEditing(tenantUserFieldRefs.current[0])
                }
                onKeyPress={(e: any) =>
                  handleOnSubmitEditing(tenantUserFieldRefs.current[0], e)
                }
              />
            )}
            {includePassword && (
              <>
                <Field
                  component={TextInput}
                  name="password"
                  label={i18n.t("labels.password") + "*"}
                  secureTextEntry
                  innerRef={(ref: any) => (passwordRef.current = ref)}
                  onSubmitEditing={() =>
                    handleOnSubmitEditing(confirmPasswordRef.current)
                  }
                  onKeyPress={(e: any) =>
                    handleOnSubmitEditing(confirmPasswordRef.current, e)
                  }
                  returnKeyType="next"
                ></Field>
                <Field
                  component={TextInput}
                  name="confirmPassword"
                  label={i18n.t("labels.confirmPassword") + "*"}
                  innerRef={(ref: any) => (confirmPasswordRef.current = ref)}
                  secureTextEntry
                  onSubmitEditing={() => handleSubmit()}
                  returnKeyType="done"
                ></Field>
              </>
            )}
            {tenantUserFields?.map((tenantUserField, i) => {
              const {
                field: { name, type },
                id,
              } = tenantUserField;
              return (
                <RenderUserFields
                  key={id}
                  name={name}
                  type={type}
                  userField={tenantUserField}
                  index={i}
                  userFieldRefs={tenantUserFieldRefs}
                  nextUserFieldRefs={planPackageUserFieldRefs}
                />
              );
            })}
            {planPackageUserFields?.map((planPackageUserField, i) => {
              const {
                field: { name, type },
                id,
              } = planPackageUserField;
              return (
                <RenderUserFields
                  key={id}
                  name={name}
                  type={type}
                  userField={planPackageUserField}
                  index={i}
                  userFieldRefs={planPackageUserFieldRefs}
                />
              );
            })}
            {TOSRequired && (
              <View style={{ marginTop: 35 }}>
                <CheckBox onChange={setTOSChecked} checked={TOSChecked}>
                  <Text style={{ textAlign: "left" }}>
                    {i18n.t("components.NewMemberForm.checkboxText") as string}{" "}
                    <Pressable onPress={() => setTOSDialog(true)}>
                      <Text style={{ color: "rgb(36, 152, 243)" }}>
                        {
                          i18n.t(
                            "components.NewMemberForm.FanRallyMarketPlaceTOS"
                          ) as string
                        }
                      </Text>
                    </Pressable>
                  </Text>
                </CheckBox>
              </View>
            )}
            <Button
              size="large"
              style={{ marginTop: 35 }}
              onPress={() => handleSubmit()}
              disabled={!isValid || (TOSRequired ? !TOSChecked : false)}
              testID="input-submit-button"
            >
              {btnText ? btnText : i18n.t("common.submit")}
            </Button>
          </View>
        )}
      </Formik>
      <FanRallyTOSDialog visible={TOSDialog} setVisible={setTOSDialog} />
    </View>
  );
};

export default NewMemberForm;
